import { Avatar, Center } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { CardV2 as Card, EmptyState } from '@feathr/components';
import { SelectedTargetings } from '@feathr/extender/components/Targetings';
import { getIconForAction } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import EstimatedReach from './AdWizardAudienceStep.EstimatedReach';

interface INewAudienceProps {
  campaign: Campaign;
  className?: string;
  targetings: ListResponse<Targeting>;
}

function NewAudience({
  campaign,
  className,
  targetings,
}: Readonly<INewAudienceProps>): JSX.Element {
  const { t } = useTranslation();
  const isLoading = campaign.isPending || targetings.isPending;

  const prefix = (
    <Center>
      <Avatar color={'neutral.5'} mb={'2'} size={'80'}>
        {getIconForAction('userGroup')}
      </Avatar>
    </Center>
  );

  const emptyState = (
    <EmptyState
      description={t(
        'Create your audience by adding location, demographic, and interest filters to reach the right people.',
      )}
      label={t('No audience selected')}
      prefix={prefix}
      theme={'neutral'}
    />
  );

  return (
    <Card contentClassName={className} data-name={'new-audience-card'} isSticky={false}>
      <Card.Header title={t('New Audience')} />
      <Card.Content isLoading={isLoading} skeleton={{ options: { paragraphs: 3 } }}>
        <SelectedTargetings
          clearButtonText={t('Clear audience')}
          emptyState={emptyState}
          targetings={targetings}
        >
          <EstimatedReach campaign={campaign} targetings={targetings} />
        </SelectedTargetings>
      </Card.Content>
    </Card>
  );
}

export default observer(NewAudience);
